import { QueryClient } from '@tanstack/react-query';

import { isApiError } from './client';

/**
 * Query defaults.
 *
 * The important one is the retry rule. TanStack retries three times by default,
 * which is right for a dropped connection and wrong for everything the API
 * answers deliberately: a 403 will still be a 403 on the third attempt, and a
 * 404 for another driver's order is a decision, not a blip. Retrying those just
 * delays the message the user needs by two seconds and triples the load on a
 * route that is already refusing.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (isApiError(error)) {
            // status 0 is a network failure — worth another go.
            if (error.status >= 400 && error.status < 500) return false;
          }
          return failureCount < 2;
        },
        staleTime: 15_000,
        // The dispatcher tabs away to answer the phone and comes back wanting
        // the board as it is now, not as it was four minutes ago.
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: {
        // Never automatically. A retried "mark delivered" is a second write of
        // something the driver only did once.
        retry: false,
      },
    },
  });
}
